import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Spin, Typography, Alert, message } from 'antd';
import { ShareAltOutlined, TeamOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/authStore';
import apiClient from '../services/api';

const { Title, Text, Paragraph } = Typography;

const JoinCollaborativeKB = () => {
  const { t } = useTranslation(['dashboard', 'messages', 'common']);
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, token } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [kbInfo, setKbInfo] = useState(null);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState(null);
  const hasAttemptedJoin = useRef(false); // 防止重复调用

  useEffect(() => {
    console.log('[加入协作库] 页面加载，shareToken:', shareToken);
    console.log('[加入协作库] 用户认证状态:', isAuthenticated);
    console.log('[加入协作库] 是否已尝试加入:', hasAttemptedJoin.current);
    
    // 如果用户未登录，重定向到登录页面，并设置返回URL
    if (!isAuthenticated || !token) {
      console.log('[加入协作库] 用户未登录，重定向到登录页面');
      const returnUrl = `/join-collaborative-kb/${shareToken}`;
      navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
      return;
    }

    // 防止重复调用：只在首次认证成功后调用一次
    if (isAuthenticated && token && !hasAttemptedJoin.current) {
      hasAttemptedJoin.current = true;
      handleJoinCollaborativeKB();
    }
  }, [shareToken, isAuthenticated, token, navigate]);

  const handleJoinCollaborativeKB = async () => {
    if (!shareToken) {
      setError(t('messages:invalid_share_link', { defaultValue: 'Invalid share link' }));
      return;
    }

    // 防止重复调用
    if (loading || joining) {
      console.log('[加入协作库] 正在处理中，忽略重复请求');
      return;
    }

    setLoading(true);
    setJoining(true);
    setError(null);

    try {
      console.log('[加入协作库] 开始加入协作库，token:', shareToken);
      const response = await apiClient.post(`/users/knowledge-base/collaborative/join/${shareToken}`);
      
      if (response.data && response.data.data) {
        const { kb, message: responseMessage } = response.data.data;
        setKbInfo(kb);
        message.success(responseMessage);
        console.log('[加入协作库] 加入成功:', kb);
        
        // 3秒后自动跳转到Dashboard，并强制刷新页面以确保数据更新
        setTimeout(() => {
          // 强制刷新页面以确保协作库列表更新
          window.location.href = '/';
        }, 3000);
      }
    } catch (error) {
      console.error('[加入协作库] 加入失败:', error);
      const errorMessage = error.response?.data?.message || error.message;
      setError(errorMessage);
      message.error(t('messages:join_collaborative_kb_failed', { defaultValue: 'Failed to join collaborative KB' }) + ': ' + errorMessage);
    } finally {
      setLoading(false);
      setJoining(false);
    }
  };

  const handleGoToDashboard = () => {
    // 强制刷新页面以确保协作库列表更新
    window.location.href = '/';
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px'
    }}>
      <Card 
        style={{ 
          width: '100%', 
          maxWidth: 500,
          textAlign: 'center',
          borderRadius: '12px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
        }}
      >
        <div style={{ marginBottom: '24px' }}>
          <ShareAltOutlined 
            style={{ 
              fontSize: '48px', 
              color: '#1890ff',
              marginBottom: '16px'
            }} 
          />
          <Title level={2} style={{ marginBottom: '8px' }}>
{t('dashboard:join_collaborative_kb', { defaultValue: 'Join Collaborative KB' })}
          </Title>
        </div>

        {loading && (
          <div style={{ padding: '40px 0' }}>
            <Spin size="large" />
            <Paragraph style={{ marginTop: '16px', color: '#666' }}>
{t('dashboard:processing_join_request', { defaultValue: 'Processing your join request...' })}
            </Paragraph>
          </div>
        )}

        {error && !loading && (
          <div style={{ padding: '20px 0' }}>
            <Alert
              message={t('messages:join_failed', { defaultValue: 'Join Failed' })}
              description={error}
              type="error"
              showIcon
              style={{ marginBottom: '20px' }}
            />
            <Button type="primary" onClick={() => navigate('/dashboard')}>
{t('common:actions.back_to_home', { defaultValue: 'Back to Home' })}
            </Button>
          </div>
        )}

        {kbInfo && !loading && (
          <div style={{ padding: '20px 0' }}>
            <Alert
              message={t('messages:join_success', { defaultValue: 'Join Successful!' })}
              description={
                <div>
                  <Paragraph style={{ margin: '8px 0' }}>
                    <TeamOutlined style={{ marginRight: '8px', color: '#52c41a' }} />
{t('dashboard:successfully_joined_collaborative_kb', { defaultValue: 'You have successfully joined the collaborative KB' })}: <strong>{kbInfo.name}</strong>
                  </Paragraph>
                  {kbInfo.description && (
                    <Paragraph style={{ margin: '8px 0', color: '#666' }}>
                      {kbInfo.description}
                    </Paragraph>
                  )}
                  <Paragraph style={{ margin: '8px 0', color: '#888', fontSize: '12px' }}>
{t('dashboard:auto_redirect_dashboard', { defaultValue: 'Page will automatically redirect to dashboard in 3 seconds...' })}
                  </Paragraph>
                </div>
              }
              type="success"
              showIcon
              style={{ marginBottom: '20px', textAlign: 'left' }}
            />
            <Button 
              type="primary" 
              size="large"
              icon={<ArrowRightOutlined />}
              onClick={handleGoToDashboard}
            >
{t('dashboard:go_to_dashboard_now', { defaultValue: 'Go to Dashboard Now' })}
            </Button>
          </div>
        )}

        {!loading && !error && !kbInfo && (
          <div style={{ padding: '20px 0' }}>
            <Paragraph style={{ color: '#666', fontSize: '14px' }}>
{t('dashboard:verifying_share_link', { defaultValue: 'Verifying share link...' })}
            </Paragraph>
          </div>
        )}
      </Card>
    </div>
  );
};

export default JoinCollaborativeKB;